/**
 * La curva S de la obra (OPUS ci, idea 11): el importe ejecutado acumulado a lo largo del tiempo, con una marca en la
 * fecha que se está viendo. SVG a mano (sin librería de gráficas), con los tokens del tema. Las fechas son reales (días
 * desde 1970, ver `diaDeFecha` en @supernova/shared): el eje X es el tiempo, no un número de semana.
 *
 * La línea llena es lo ejecutado hasta la fecha de la marca; la tenue, lo que viene. La línea punteada es el importe
 * contratado, para ver cuánto falta.
 */
import { useMemo } from 'react';
import { interpolar, nudosDeAvance, type AvanceObraPeriodo } from '@supernova/shared';
import { diaDeFecha } from '@supernova/shared';
import { etiquetaDeDia, etiquetaDeMes } from '../../lib/avanceObra4d.js';

const ANCHO = 420;
const ALTO = 190;
const M = { izq: 62, der: 18, arr: 12, abajo: 26 };

interface Props {
  periodos: readonly AvanceObraPeriodo[];
  desde: number;
  hasta: number;
  /** La fecha que se está viendo (días). */
  t: number;
  /** El importe contratado total (la línea punteada); 0 o ausente = sin línea. */
  contratado?: number;
  /** Formato corto de dinero (sin decimales). */
  moneyCorto: (n: number) => string;
  /** Al arrastrar o tocar la curva, la fecha elegida (días). */
  onElegirFecha?: (t: number) => void;
}

export function CurvaS({ periodos, desde, hasta, t, contratado = 0, moneyCorto, onElegirFecha }: Props) {
  const geom = useMemo(() => {
    const acum = new Map(periodos.map((p) => [p.numero, p.importeAcumulado] as const));
    const nudos = nudosDeAvance(periodos, acum);
    const tope = Math.max(contratado, ...periodos.map((p) => p.importeAcumulado), 1);
    const dias = Math.max(hasta - desde, 1);
    const x = (d: number) => M.izq + ((d - desde) / dias) * (ANCHO - M.izq - M.der);
    const y = (v: number) => ALTO - M.abajo - (v / tope) * (ALTO - M.abajo - M.arr);
    // La curva se dibuja en pasos de 1/80 del rango: suficiente para que las rectas de cada periodo se vean rectas.
    const pasos = 80;
    const pts: { d: number; v: number }[] = [];
    for (let i = 0; i <= pasos; i++) {
      const d = desde + (dias * i) / pasos;
      pts.push({ d, v: interpolar(nudos, d) });
    }
    return { nudos, tope, dias, x, y, pts };
  }, [periodos, desde, hasta, contratado]);

  const { x, y, tope, pts, nudos } = geom;
  const tt = Math.max(desde, Math.min(hasta, t));
  const camino = (lista: { d: number; v: number }[]) => lista.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.d).toFixed(1)},${y(p.v).toFixed(1)}`).join(' ');
  const hecho = pts.filter((p) => p.d <= tt);
  hecho.push({ d: tt, v: interpolar(nudos, tt) });
  const ahora = interpolar(nudos, tt);
  // Un rótulo de mes por cada cierre de periodo, sin amontonarlos: como mucho 6.
  const cierres = periodos.map((p) => diaDeFecha(p.fechaFinal));
  const cada = Math.max(1, Math.ceil(cierres.length / 6));
  const rotulos = cierres.filter((_, i) => i % cada === 0 || i === cierres.length - 1);

  const elegir = (ev: { currentTarget: SVGSVGElement; clientX: number }) => {
    if (!onElegirFecha) return;
    const r = ev.currentTarget.getBoundingClientRect();
    const px = ((ev.clientX - r.left) / r.width) * ANCHO;
    const d = desde + ((px - M.izq) / (ANCHO - M.izq - M.der)) * (hasta - desde);
    onElegirFecha(Math.max(desde, Math.min(hasta, Math.round(d))));
  };

  return (
    <svg
      viewBox={`0 0 ${ANCHO} ${ALTO}`}
      role="img"
      aria-label={`Curva S: importe ejecutado ${moneyCorto(ahora)} al ${etiquetaDeDia(tt)}`}
      style={{ width: '100%', display: 'block', cursor: onElegirFecha ? 'pointer' : 'default' }}
      onClick={elegir}
    >
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <g key={f}>
          <line x1={M.izq} x2={ANCHO - M.der} y1={y(tope * f)} y2={y(tope * f)} stroke="var(--border-subtle)" strokeWidth={1} />
          <text x={M.izq - 6} y={y(tope * f) + 3} textAnchor="end" fontSize={9} fill="var(--text-tertiary)">
            {moneyCorto(tope * f)}
          </text>
        </g>
      ))}
      {contratado > 0 && (
        <line x1={M.izq} x2={ANCHO - M.der} y1={y(contratado)} y2={y(contratado)} stroke="var(--text-secondary)" strokeWidth={1} strokeDasharray="4 3" />
      )}
      <path d={camino(pts)} fill="none" stroke="var(--accent)" strokeWidth={2} opacity={0.3} />
      <path d={camino(hecho)} fill="none" stroke="var(--accent)" strokeWidth={2.5} />
      {rotulos.map((d) => (
        <text key={d} x={x(d)} y={ALTO - 8} textAnchor={d === cierres[cierres.length - 1] ? 'end' : 'middle'} fontSize={9} fill="var(--text-tertiary)">
          {etiquetaDeMes(d)}
        </text>
      ))}
      <line x1={x(tt)} x2={x(tt)} y1={M.arr} y2={ALTO - M.abajo} stroke="var(--warn)" strokeWidth={1.5} />
      <circle cx={x(tt)} cy={y(ahora)} r={4} fill="var(--warn)" />
    </svg>
  );
}
